import type {
  BreakdownByType,
  MetricsBySede,
  TotalRiskLevelByRiskIndicatorWithPercentage,
} from '@/domains/home/home.entity';
import {
  type Day,
  type RiskIndicatorName,
  riskLevelColors,
  typesOfRisk,
} from '@/domains/shared';
import { calculatePercentage } from '@/shared/utils';

/**
 * Use Case para calcular indicadores de riesgo globales
 * Cumple SRP: Solo se encarga de agregar y calcular indicadores de riesgo para gráficos
 *
 * Responsabilidades:
 * - Opcionalmente filtrar métricas por jornada activa
 * - Agregar indicadores de riesgo de todas las métricas
 * - Sumar niveles de riesgo (rojo, amarillo, verde) por cada tipo de indicador
 * - Calcular porcentajes de cada nivel sobre el total del indicador específico
 * - Validar integridad de datos de entrada
 *
 * Encapsula la lógica de negocio de agregación de indicadores para gráficos
 * Diseñado para proveer datos procesados listos para visualización en UI
 */
export class CalculateRiskIndicatorsUseCase {
  /**
   * Calcula totales y porcentajes de indicadores de riesgo globales
   * Agrega todos los tipos de riesgo (Inasistencias, Notas, etc.) con sus niveles (rojo, amarillo, verde)
   * Opcionalmente filtra por jornada antes de calcular
   * @param data - Array de métricas por sede que contienen desglose por tipo de riesgo
   * @param totalStudents - Total de estudiantes (parámetro mantenido por compatibilidad, no se usa)
   * @param activeJornada - Jornada activa opcional para filtrado ('V' vespertino, 'D' diurno, null para todas)
   * @returns Objeto con totales y porcentajes por tipo de riesgo y nivel de severidad
   * @throws Error si los datos no son válidos o totalStudents es negativo
   */
  execute(
    data: MetricsBySede[],
    totalStudents: number,
    activeJornada?: Day | null,
  ): TotalRiskLevelByRiskIndicatorWithPercentage {
    if (!Array.isArray(data)) {
      throw new Error('Los datos deben ser un array válido');
    }

    if (totalStudents < 0) {
      throw new Error('El total de estudiantes no puede ser negativo');
    }

    const filteredData = activeJornada
      ? data.filter((item) => item.jornada === activeJornada)
      : data;

    const acumulado: Record<RiskIndicatorName, BreakdownByType> =
      typesOfRisk.reduce(
        (acc, tipo) => {
          acc[tipo] = { rojo: 0, amarillo: 0, verde: 0 };
          return acc;
        },
        {} as Record<RiskIndicatorName, BreakdownByType>,
      );

    for (const metric of filteredData) {
      for (const typeOfRisk of typesOfRisk) {
        for (const riskLevelColor of riskLevelColors) {
          acumulado[typeOfRisk][riskLevelColor] +=
            metric.desglose_por_tipo[typeOfRisk][riskLevelColor];
        }
      }
    }

    const resultado: TotalRiskLevelByRiskIndicatorWithPercentage =
      {} as TotalRiskLevelByRiskIndicatorWithPercentage;

    for (const typeOfRisk of typesOfRisk) {
      const totalForIndicator =
        acumulado[typeOfRisk].rojo +
        acumulado[typeOfRisk].amarillo +
        acumulado[typeOfRisk].verde;

      resultado[typeOfRisk] = {
        rojo: {
          total: acumulado[typeOfRisk].rojo,
          percentage: calculatePercentage(
            acumulado[typeOfRisk].rojo,
            totalForIndicator,
          ),
        },
        amarillo: {
          total: acumulado[typeOfRisk].amarillo,
          percentage: calculatePercentage(
            acumulado[typeOfRisk].amarillo,
            totalForIndicator,
          ),
        },
        verde: {
          total: acumulado[typeOfRisk].verde,
          percentage: calculatePercentage(
            acumulado[typeOfRisk].verde,
            totalForIndicator,
          ),
        },
      };
    }

    return resultado;
  }
}
